<script setup lang="ts">
import TooltipDialog from '@/core/dialogs/TooltipDialog.vue';
import type { IRequiredActionPrompt } from '@/composables/requiredAction/requiredAction.types';
import {
  REQUIRED_ACTION_PULSE_ANIMATION_NAME,
  REQUIRED_ACTION_PULSE_DURATION_MS,
} from '@/constants/required-action.constants';
import { EPosition } from 'common/enum/misc.enum';
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  dismiss: [];
}>();

const props = withDefaults(
  defineProps<{
    prompt?: IRequiredActionPrompt | null;
  }>(),
  {
    prompt: null,
  },
);

const anchorRef = useTemplateRef<HTMLElement>('anchorRef');
const tooltipRef = useTemplateRef<InstanceType<typeof TooltipDialog>>('tooltipRef');
const tooltipId = `required-action-tooltip-${useId()}`;
let pulseTimer: ReturnType<typeof setTimeout> | null = null;
const pulsing = ref(false);
const highlighted = ref(false);

const clearPulseTimer = (): void => {
  if (pulseTimer === null) return;
  clearTimeout(pulseTimer);
  pulseTimer = null;
};

const finishPulse = (): void => {
  clearPulseTimer();
  pulsing.value = false;
  highlighted.value = false;
};

const handleAnimationEnd = (event: AnimationEvent): void => {
  if (event.animationName === REQUIRED_ACTION_PULSE_ANIMATION_NAME) finishPulse();
};

const targetClass = computed(() => [
  'transition-colors',
  highlighted.value ? '!bg-highlight !duration-0' : 'duration-1000',
  pulsing.value && 'motion-safe:animate-required-action-pulse',
]);

const retriggerPulse = async (): Promise<void> => {
  clearPulseTimer();
  pulsing.value = false;
  highlighted.value = false;
  await nextTick();
  highlighted.value = true;
  pulsing.value = true;
  pulseTimer = setTimeout(finishPulse, REQUIRED_ACTION_PULSE_DURATION_MS);
};

const handleInteraction = (): void => {
  emit('dismiss');
};

const handleTooltipHide = (): void => {
  if (props.prompt?.showTooltip) emit('dismiss');
};

watch(
  () => props.prompt?.animationSequence,
  (sequence, previousSequence) => {
    if (sequence === undefined || sequence === previousSequence) return;
    void retriggerPulse();
  },
);

watch(
  () => props.prompt?.showTooltip,
  async (showTooltip) => {
    await nextTick();
    if (showTooltip) tooltipRef.value?.show();
    else tooltipRef.value?.hide();
  },
);

onBeforeUnmount(clearPulseTimer);
</script>

<template>
  <span
    ref="anchorRef"
    class="relative inline-flex min-w-0"
    @animationend="handleAnimationEnd"
    @focusin.capture="handleInteraction"
    @pointerdown.capture="handleInteraction"
  >
    <slot :target-class="targetClass" :described-by="props.prompt?.showTooltip ? tooltipId : undefined"></slot>
  </span>

  <TooltipDialog
    v-if="props.prompt"
    :id="tooltipId"
    ref="tooltipRef"
    role="tooltip"
    :anchor-element="anchorRef"
    :position="EPosition.TOP_CENTER"
    flip-strategy="vertical"
    persistent
    content-style="w-64 !rounded-badge !px-lg !py-md text-base font-normal shadow-sm"
    @hide="handleTooltipHide"
  >
    <template #content>{{ props.prompt.message }}</template>
  </TooltipDialog>
</template>
